import { Injectable } from '@angular/core';
import { HomesFacade } from '@library/store/homes/homes.facade';
import { endOfApiScale } from '@library/store/measures/measures';
import { MeasuresLoaderService } from '@library/store/measures/measures-loader.service';
import { EnumApiScales, EnumMeasureTypes, EnumScales, TimeRange } from '@library/store/measures/measures.interface';
import { ModulesFacade } from '@library/store/modules/modules.facade';
import { ConsumptionType, Module, SourceType } from '@library/store/modules/modules.interface';
import { ControlPageStore } from '@pages/control-page/control-page-store.service';
import { FAVORITE_HUMIDITY_MODULES, FAVORITE_TEMPERATURE_MODULES, SUPPORTED_CONTROL_ENERGY_MODULES, SUPPORTED_CONTROL_WEATHER_MODULES } from '@pages/control-page/control-page.const';
import { Enums } from 'constants/Enums.constant';
import * as moment from 'moment-timezone';
import { Subscription, filter, firstValueFrom } from 'rxjs';

@Injectable()
export class PreviewsMeasuresLoaderService {

  private sub = new Subscription();

  constructor(
    private homesFacade: HomesFacade,
    private modulesFacade: ModulesFacade,
    private measuresLoaderService: MeasuresLoaderService,
    private controlPageStore: ControlPageStore,
  ) {}

  subscribe() {
    this.homesFacade.currentHomeId$
      .pipe(filter(id => !!id))
      .subscribe(() => this.load());
  }

  async load() {
    const modules = await firstValueFrom(this.modulesFacade.currentHomeModules$);

    this.loadMeasures(
      modules,
      EnumScales.DAY,
      {
        dateBegin: moment().startOf('hour').subtract(23, 'hours'),
        dateEnd: endOfApiScale(moment(), EnumApiScales.FIVE_MINUTES),
      },
    );
  }

  async loadMeasures(modules: Module[], scale: EnumScales, timeRange: TimeRange) {
    this.measuresLoaderService.loadHomeElecMeasures(
      [
        EnumMeasureTypes.SUM_ENERGY_ELEC_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_ELEC_BASE,
        EnumMeasureTypes.SUM_ENERGY_ELEC_PEAK,
        EnumMeasureTypes.SUM_ENERGY_ELEC_OFFPEAK,
        EnumMeasureTypes.SUM_ENERGY_PRICE_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_PRICE_BASE,
        EnumMeasureTypes.SUM_ENERGY_PRICE_PEAK,
        EnumMeasureTypes.SUM_ENERGY_PRICE_OFFPEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_BASE,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_OFFPEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_NO_CONTRACT,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_BASE,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_PEAK,
        EnumMeasureTypes.SUM_ENERGY_BUY_FROM_GRID_PRICE_OFFPEAK,
      ],
      this.controlPageStore.scaleToApiScale(scale),
      timeRange,
    );

    const fluidLines = modules.filter(m => m.consumption_type === ConsumptionType.FLUID);
    this.measuresLoaderService.loadModulesMeasures(
      fluidLines,
      fluidLines.reduce((acc, curr) => {
        let mType: EnumMeasureTypes[];
        switch (curr.source_type) {
          case SourceType.COLD_WATER:
            mType = [
              EnumMeasureTypes.SUM_FLUID_CONSUMPTION_COLD_WATER,
              EnumMeasureTypes.SUM_FLUID_PRICE_COLD_WATER,
            ];
            break;
          case SourceType.HOT_WATER:
            mType = [
              EnumMeasureTypes.SUM_FLUID_CONSUMPTION_HOT_WATER,
              EnumMeasureTypes.SUM_FLUID_PRICE_HOT_WATER,
            ];
            break;
          case SourceType.GAS:
            mType = [
              EnumMeasureTypes.SUM_FLUID_CONSUMPTION_GAS,
              EnumMeasureTypes.SUM_FLUID_PRICE_GAS,
            ];
            break;
        }
        if (typeof mType !== 'undefined') {
          acc = {
            ...acc,
            [curr.id]: mType
          };
        }

        return acc;
      }, {}),
      this.controlPageStore.scaleToApiScale(scale),
      timeRange,
    );

    const home = await firstValueFrom(this.homesFacade.currentHome$);

    const weatherModules = modules.filter(m => {
      if (home.mode !== 'favorite') {
        return SUPPORTED_CONTROL_WEATHER_MODULES.includes(m.type);
      }
      else {
        return [...FAVORITE_TEMPERATURE_MODULES, ...FAVORITE_HUMIDITY_MODULES].includes(m.type);
      }
    });
    this.measuresLoaderService.loadModulesMeasures(
      weatherModules,
      weatherModules.reduce((acc, curr) => {
        acc = {
          ...acc,
          [curr.id]: curr.mode !== 'favorite' ? MODULE_MEASURE_TYPES[curr.type] : FAVORITE_MODULE_MEASURE_TYPES[curr.type],
        };
        return acc;
      }, {}),
      this.controlPageStore.scaleToApiScale(scale),
      timeRange,
    );

    const energyModules = modules.filter(m => (SUPPORTED_CONTROL_ENERGY_MODULES.includes(m.type)));
    this.measuresLoaderService.loadModulesMeasures(
      energyModules,
      energyModules.reduce((acc, curr) => {
        acc = {
          ...acc,
          [curr.id]: MODULE_MEASURE_TYPES[curr.type],
        };
        return acc;
      }, {}),
      this.controlPageStore.scaleToApiScale(scale),
      timeRange,
    );
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }

}

const MODULE_MEASURE_TYPES = {
  [Enums.ModuleType.NAMAIN]: [
    EnumMeasureTypes.TEMPERATURE,
    EnumMeasureTypes.MIN_TEMPERATURE,
    EnumMeasureTypes.MAX_TEMPERATURE,
    EnumMeasureTypes.HUMIDITY,
    EnumMeasureTypes.MAX_HUMIDITY,
    EnumMeasureTypes.MIN_HUMIDITY,
    EnumMeasureTypes.CO2,
    EnumMeasureTypes.MIN_CO2,
    EnumMeasureTypes.MAX_CO2,
    EnumMeasureTypes.PRESSURE,
    EnumMeasureTypes.NOISE,
    EnumMeasureTypes.MIN_NOISE,
    EnumMeasureTypes.MAX_NOISE,
  ],
  // Outdoor
  [Enums.ModuleType.NAMODULE1]: [
    EnumMeasureTypes.TEMPERATURE,
    EnumMeasureTypes.MIN_TEMPERATURE,
    EnumMeasureTypes.MAX_TEMPERATURE,
    EnumMeasureTypes.HUMIDITY,
    EnumMeasureTypes.MAX_HUMIDITY,
    EnumMeasureTypes.MIN_HUMIDITY,
  ],
  // Anemo
  [Enums.ModuleType.NAMODULE2]: [
    EnumMeasureTypes.WIND_STRENGTH,
    EnumMeasureTypes.GUST_STRENGTH,
    EnumMeasureTypes.WIND_ANGLE,
    EnumMeasureTypes.GUST_ANGLE,
  ],
  // Rain
  [Enums.ModuleType.NAMODULE3]: [
    EnumMeasureTypes.SUM_RAIN,
  ],
  // Indoor
  [Enums.ModuleType.NAMODULE4]: [
    EnumMeasureTypes.TEMPERATURE,
    EnumMeasureTypes.MIN_TEMPERATURE,
    EnumMeasureTypes.MAX_TEMPERATURE,
    EnumMeasureTypes.HUMIDITY,
    EnumMeasureTypes.MAX_HUMIDITY,
    EnumMeasureTypes.MIN_HUMIDITY,
    EnumMeasureTypes.CO2,
    EnumMeasureTypes.MIN_CO2,
    EnumMeasureTypes.MAX_CO2,
    EnumMeasureTypes.PRESSURE,
  ],
  // Thermostat
  [Enums.ModuleType.NATHERM1]: [
    EnumMeasureTypes.TEMPERATURE,
    EnumMeasureTypes.MIN_TEMPERATURE,
    EnumMeasureTypes.MAX_TEMPERATURE,
    EnumMeasureTypes.SP_TEMPERATURE,
  ],
  // Valve
  [Enums.ModuleType.NRV]: [
    EnumMeasureTypes.TEMPERATURE,
    EnumMeasureTypes.MIN_TEMPERATURE,
    EnumMeasureTypes.MAX_TEMPERATURE,
    EnumMeasureTypes.SP_TEMPERATURE,
  ],
};

const FAVORITE_MODULE_MEASURE_TYPES = {
  [Enums.ModuleType.NAMAIN]: [
    EnumMeasureTypes.PRESSURE,
  ],
  // Outdoor
  [Enums.ModuleType.NAMODULE1]: [
    EnumMeasureTypes.TEMPERATURE,
    EnumMeasureTypes.MIN_TEMPERATURE,
    EnumMeasureTypes.MAX_TEMPERATURE,
    EnumMeasureTypes.HUMIDITY,
    EnumMeasureTypes.MAX_HUMIDITY,
    EnumMeasureTypes.MIN_HUMIDITY,
  ],
  // Anemo
  [Enums.ModuleType.NAMODULE2]: [
    EnumMeasureTypes.WIND_STRENGTH,
    EnumMeasureTypes.GUST_STRENGTH,
    EnumMeasureTypes.WIND_ANGLE,
    EnumMeasureTypes.GUST_ANGLE,
  ],
  // Rain
  [Enums.ModuleType.NAMODULE3]: [
    EnumMeasureTypes.SUM_RAIN,
  ],
};

